import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions, DialogContent, styled, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TPartnerFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { UploadErrorMessage } from 'src/components/hook-form/UploadErrorMessage';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import Iconify from 'src/components/iconify';
import {
  FormInnerContainer,
  FormInnerContainerRow,
  FormItem,
  FormLabel,
  FormRow,
} from 'src/layouts/form/FormLayout';
import { IPartner, useStores } from 'src/models';
import { partnerApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import partnerFormValidationSchema from 'src/static/yup/partnerFormValidationSchema';
import { getFileUrl, separateFilesAndUrls } from 'src/utils/fileUploadUtils';
import { getPartnerFormDefaultValues } from 'src/utils/getRHFDefaultValues';

interface IPartnerFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IPartner;
}

export const PartnerFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IPartnerFormDialogProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, commonCodeStore } = useStores();
    const { formId } = useDialogContext();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;
    const [file, setFile] = useState<Array<File | string>>([]);
    const methods = useForm<TPartnerFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      resolver: yupResolver(partnerFormValidationSchema),
      defaultValues: getPartnerFormDefaultValues(selectedData),
    });
    const {
      formState: { errors },
    } = methods;

    useEffect(() => {
      setFile(selectedData?.logoImgPath ? [selectedData.logoImgPath] : []);
    }, []);

    const PARTNER_CTGY_CD = commonCodeStore.getCCodeByName('PARTNER_CTGY_CD');

    // 등록 / 수정 함수
    const onValid = async (data: TPartnerFormData) => {
      const { uploadedFiles, paths } = separateFilesAndUrls(file);
      const formData: TPartnerFormData = {
        // 공통
        partnerName: data.partnerName,
        useYn: data.useYn,
        partnerCtgyCd: data.partnerCtgyCd,
        ordr: data.ordr,
        ...(uploadedFiles.length > 0 && {
          logoImgFile: uploadedFiles[0],
          ...(!isCreate && { deleteLogoImgPath: selectedData?.logoImgPath ?? '' }),
        }),
        ...(paths.length > 0 && { logoImgPath: paths[0] }),
      };

      const result = isCreate
        ? partnerApi.post(formData)
        : partnerApi.put(selectedData?.partnerSid ?? 0, formData);
      const responseText = isCreate ? '등록' : '수정';
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (selectedData?.partnerSid) {
          partnerApi
            .remove(selectedData.partnerSid)
            .then(() =>
              handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
            );
        }
      });
      alertStore.setProps({
        open: true,
        title: '파트너 삭제',
        content: '해당 파트너를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainerRow>
            <FormInnerContainer sx={{ width: '100%' }}>
              <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFSwitch label={t('partner.form.useYn')} name="useYn" disabled={readonly} />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFSelect
                    label={t('partner.form.categoryType')}
                    name="partnerCtgyCd"
                    options={PARTNER_CTGY_CD ?? []}
                    disabled={readonly}
                    useAll={false}
                  />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField
                    label={t('partner.form.partnerName')}
                    name="partnerName"
                    disabled={readonly}
                  />
                </FormItem>
              </FormRow>
            </FormInnerContainer>
            <FormInnerContainer>
              <FormLabel>{t('partner.form.logoImage')}</FormLabel>
              <Box sx={{ height: '100%' }}>
                {file.length > 0 ? (
                  <ImageContainer>
                    <img
                      width={'100%'}
                      src={getFileUrl(file[0])}
                      style={{ maxWidth: 300, minWidth: 200, maxHeight: 170, objectFit: 'cover' }}
                      alt="logo-image"
                    />
                    <DeleteButton onClick={() => setFile([])}>
                      <Iconify icon="eva:close-fill" width={20} color={theme.palette.grey[300]} />
                    </DeleteButton>
                  </ImageContainer>
                ) : (
                  <div
                    style={{
                      height: '100%',
                      minHeight: 170,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <RHFUploadImage
                      name="logoImgPath"
                      files={file}
                      onChange={setFile}
                      accept={{ 'image/*': [] }}
                      maxFiles={1}
                    />
                    {errors.logoImgPath?.message && (
                      <UploadErrorMessage message={String(errors.logoImgPath.message)} />
                    )}
                  </div>
                )}
              </Box>
            </FormInnerContainer>
          </FormInnerContainerRow>
        </DialogContent>
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: 1,
  borderRadius: 1,
  backgroundColor: 'rgba(0, 0, 0, 0.09)',
  width: '100%',
  height: 180,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  cursor: 'pointer',
  position: 'absolute',
  top: 0,
  right: 0,
  width: 30,
  minWidth: 30,
  height: 30,
  backgroundColor: theme.palette.grey[700],
  borderRadius: 1,
  padding: '3px',
  opacity: 0.8,
  '&:hover': { opacity: 1, backgroundColor: theme.palette.grey[900] },
}));
